import {
  API_ROUTES,
  balanceSheetRowSchema,
  bankReconciliationRowSchema,
  formatMinor,
  payableRowSchema,
  receivableRowSchema,
  trialBalanceRowSchema,
  type BalanceSheetRow,
  type BankReconciliationRow,
  type PayableRow,
  type ReceivableRow,
  type TrialBalanceRow,
} from '@hvac/contracts';
import { Card, CardBody, CardHeader, Skeleton } from '@hvac/ui';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { Fragment, useState } from 'react';
import { z } from 'zod';
import { apiClient } from '../../shared/api/queries.js';

/**
 * The books, as an accountant reads them: balance sheet, bank reconciliation,
 * trial balance, at any date. Every figure is read from the ledger, which the
 * database keeps balanced, so the sentences here check what the database
 * already guarantees and say so plainly.
 */

type View = 'balance_sheet' | 'reconciliation' | 'receivables' | 'payables' | 'trial_balance';

const VIEWS: readonly { key: View; label: string }[] = [
  { key: 'balance_sheet', label: 'Balance sheet' },
  { key: 'reconciliation', label: 'Bank reconciliation' },
  { key: 'receivables', label: 'Owed to us' },
  { key: 'payables', label: 'We owe' },
  { key: 'trial_balance', label: 'Trial balance' },
];

const atDate = <T,>(
  organizationId: string,
  name: string,
  path: string,
  asOf: string,
  row: z.ZodType<T>,
) =>
  queryOptions({
    queryKey: ['reports', organizationId, name, asOf] as const,
    queryFn: ({ signal }) =>
      apiClient.request({ path: `${path}?asOf=${asOf}`, schema: z.array(row), signal }),
    staleTime: 60_000,
  });

const add = (values: readonly string[]): bigint =>
  values.reduce((sum, value) => sum + BigInt(value), 0n);

/** Assets against liabilities plus equity, in a sentence. */
export function balanceSheetSentence(rows: readonly BalanceSheetRow[]): string {
  if (rows.length === 0) return 'Nothing is in the books at this date yet.';
  const of = (section: BalanceSheetRow['section']) =>
    add(rows.filter((row) => row.section === section).map((row) => row.amountMinor));
  const assets = of('asset');
  const claims = of('liability') + of('equity');
  if (assets !== claims) {
    return `Assets ${formatMinor(assets.toString())} but liabilities and equity ${formatMinor(claims.toString())}. This should never happen; report it.`;
  }
  return `Assets of ${formatMinor(assets.toString())}, equal to what is owed plus what the owner has in the business. It balances.`;
}

/** Which accounts reconcile, which do not, and what is still waiting. */
export function reconciliationSentence(rows: readonly BankReconciliationRow[]): string {
  if (rows.length === 0) return 'No bank or card accounts yet. Import a statement to add one.';
  const off = rows.filter((row) => row.differenceMinor !== '0');
  const waiting = rows.reduce((sum, row) => sum + row.waitingCount, 0);
  const waitingPart =
    waiting === 0
      ? 'Every statement line is accounted for.'
      : `${String(waiting)} statement line${waiting === 1 ? ' is' : 's are'} still waiting to be matched or explained.`;
  if (off.length === 0) return `Every account reconciles to its statements. ${waitingPart}`;
  const names = off.map((row) => row.accountName).join(', ');
  return `${names} ${off.length === 1 ? 'does' : 'do'} not reconcile yet. ${waitingPart}`;
}

const ACCOUNT_TYPE_ORDER = [
  'asset',
  'liability',
  'equity',
  'revenue',
  'cost_of_goods_sold',
  'expense',
];

export function LedgerCard({ organizationId }: { organizationId: string }) {
  const [view, setView] = useState<View>('balance_sheet');
  const [asOf, setAsOf] = useState(() => new Date().toISOString().slice(0, 10));

  const sheet = useQuery({
    ...atDate(
      organizationId,
      'balance-sheet',
      API_ROUTES.reports.balanceSheet(organizationId),
      asOf,
      balanceSheetRowSchema,
    ),
    enabled: view === 'balance_sheet',
  });
  const recon = useQuery({
    ...atDate(
      organizationId,
      'bank-reconciliation',
      API_ROUTES.reports.bankReconciliation(organizationId),
      asOf,
      bankReconciliationRowSchema,
    ),
    enabled: view === 'reconciliation',
  });
  const owed = useQuery({
    ...atDate(
      organizationId,
      'receivables',
      API_ROUTES.reports.receivables(organizationId),
      asOf,
      receivableRowSchema,
    ),
    enabled: view === 'receivables',
  });
  const owe = useQuery({
    ...atDate(
      organizationId,
      'payables',
      API_ROUTES.reports.payables(organizationId),
      asOf,
      payableRowSchema,
    ),
    enabled: view === 'payables',
  });
  const trial = useQuery({
    ...atDate(
      organizationId,
      'trial-balance',
      API_ROUTES.reports.trialBalance(organizationId),
      asOf,
      trialBalanceRowSchema,
    ),
    enabled: view === 'trial_balance',
  });

  const description =
    view === 'balance_sheet'
      ? sheet.data === undefined
        ? 'Reading the ledger…'
        : balanceSheetSentence(sheet.data)
      : view === 'reconciliation'
        ? recon.data === undefined
          ? 'Reading the ledger…'
          : reconciliationSentence(recon.data)
        : view === 'receivables'
          ? owed.data === undefined
            ? 'Reading the ledger…'
            : receivablesSentence(owed.data)
          : view === 'payables'
            ? owe.data === undefined
              ? 'Reading the ledger…'
              : payablesSentence(owe.data)
            : trial.data === undefined
              ? 'Reading the ledger…'
              : trialSentence(trial.data);

  const pending =
    (view === 'balance_sheet' && sheet.isPending) ||
    (view === 'reconciliation' && recon.isPending) ||
    (view === 'receivables' && owed.isPending) ||
    (view === 'payables' && owe.isPending) ||
    (view === 'trial_balance' && trial.isPending);

  return (
    <Card>
      <CardHeader title="The books" description={description} />
      <CardBody className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1" role="group" aria-label="Report">
            {VIEWS.map((option) => (
              <button
                key={option.key}
                type="button"
                aria-pressed={view === option.key}
                onClick={() => setView(option.key)}
                className={[
                  'rounded-[var(--radius-control)] px-3 py-1 text-sm',
                  view === option.key
                    ? 'bg-brand-50 font-medium text-brand-700'
                    : 'text-content-muted hover:bg-surface-sunken',
                ].join(' ')}
              >
                {option.label}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-2 text-sm text-content-muted">
            As of
            <input
              type="date"
              value={asOf}
              onChange={(event) => {
                if (event.target.value !== '') setAsOf(event.target.value);
              }}
              className="rounded-[var(--radius-control)] border border-border bg-surface px-2 py-1 text-content"
            />
          </label>
        </div>

        {pending ? (
          <Skeleton className="h-48 w-full" />
        ) : view === 'balance_sheet' ? (
          <BalanceSheet rows={sheet.data ?? []} />
        ) : view === 'reconciliation' ? (
          <Reconciliation rows={recon.data ?? []} />
        ) : view === 'receivables' ? (
          <Receivables rows={owed.data ?? []} />
        ) : view === 'payables' ? (
          <Payables rows={owe.data ?? []} />
        ) : (
          <TrialBalance rows={trial.data ?? []} />
        )}
      </CardBody>
    </Card>
  );
}

/** Who owes, how much, and how much of it is late, in a sentence. */
export function receivablesSentence(rows: readonly ReceivableRow[]): string {
  if (rows.length === 0) return 'Nobody owes anything at this date. Every invoice is paid.';
  const total = add(rows.map((row) => row.owedMinor));
  const late = add(
    rows
      .filter((row) => row.bucket === '61-90' || row.bucket === '90+')
      .map((row) => row.owedMinor),
  );
  const customers = new Set(rows.map((row) => row.customerId ?? row.transactionId)).size;
  const who = `${String(customers)} customer${customers === 1 ? '' : 's'}`;
  return late === 0n
    ? `${who} owe ${formatMinor(total.toString())} across ${String(rows.length)} invoice${rows.length === 1 ? '' : 's'}, none of it more than 60 days old.`
    : `${who} owe ${formatMinor(total.toString())}; ${formatMinor(late.toString())} of it is more than 60 days old.`;
}

function trialSentence(rows: readonly TrialBalanceRow[]): string {
  if (rows.length === 0) return 'Nothing is in the books at this date yet.';
  const debits = add(rows.map((row) => row.debitMinor));
  const credits = add(rows.map((row) => row.creditMinor));
  return debits === credits
    ? `${String(rows.length)} accounts, debits and credits both ${formatMinor(debits.toString())}. It balances.`
    : `Debits ${formatMinor(debits.toString())} against credits ${formatMinor(credits.toString())}. This should never happen; report it.`;
}

const Label = ({ code, name }: { code: string | null; name: string }) => (
  <>
    {code === null ? null : <span className="text-content-muted">{code}</span>} {name}
  </>
);

function BalanceSheet({ rows }: { rows: readonly BalanceSheetRow[] }) {
  if (rows.length === 0) {
    return (
      <p className="text-sm text-content-muted">
        The balance sheet fills in as records are signed and bank lines matched.
      </p>
    );
  }
  const sections: readonly [BalanceSheetRow['section'], string][] = [
    ['asset', 'Assets'],
    ['liability', 'Liabilities'],
    ['equity', 'Equity'],
  ];
  return (
    <table className="w-full text-sm tabular">
      <tbody>
        {sections.map(([section, title]) => {
          const lines = rows.filter((row) => row.section === section);
          return (
            <SectionRows
              key={section}
              title={title}
              total={add(lines.map((row) => row.amountMinor))}
              lines={lines.map((row) => ({
                key: row.accountId ?? row.accountName,
                label: <Label code={row.accountCode} name={row.accountName} />,
                amount: row.amountMinor,
              }))}
            />
          );
        })}
      </tbody>
    </table>
  );
}

function SectionRows({
  title,
  total,
  lines,
}: {
  title: string;
  total: bigint;
  lines: readonly { key: string; label: React.ReactNode; amount: string }[];
}) {
  return (
    <>
      <tr>
        <td colSpan={2} className="pt-3 pb-1 text-xs font-medium uppercase text-content-muted">
          {title}
        </td>
      </tr>
      {lines.map((line) => (
        <tr key={line.key}>
          <td className="py-0.5">{line.label}</td>
          <td className="py-0.5 text-right">{formatMinor(line.amount)}</td>
        </tr>
      ))}
      <tr className="border-t border-border font-medium">
        <td className="py-1">Total {title.toLowerCase()}</td>
        <td className="py-1 text-right">{formatMinor(total.toString())}</td>
      </tr>
    </>
  );
}

function Reconciliation({ rows }: { rows: readonly BankReconciliationRow[] }) {
  if (rows.length === 0) return null;
  return (
    <div className="flex flex-col gap-3">
      <table className="w-full text-sm tabular">
        <thead>
          <tr className="text-left text-xs text-content-muted">
            <th className="py-1 font-medium">Account</th>
            <th className="py-1 text-right font-medium">Statement lines</th>
            <th className="py-1 text-right font-medium">In the books</th>
            <th className="py-1 text-right font-medium">Waiting</th>
            <th className="py-1 text-right font-medium">Difference</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.financialAccountId}>
              <td className="py-0.5">{row.accountName}</td>
              <td className="py-0.5 text-right">{formatMinor(row.statementMinor)}</td>
              <td className="py-0.5 text-right">{formatMinor(row.booksMinor)}</td>
              <td className="py-0.5 text-right">
                {row.waitingCount === 0
                  ? '—'
                  : `${formatMinor(row.waitingMinor)} (${String(row.waitingCount)})`}
              </td>
              <td
                className={[
                  'py-0.5 text-right',
                  row.differenceMinor === '0' ? 'text-success' : 'font-medium text-danger',
                ].join(' ')}
              >
                {row.differenceMinor === '0' ? 'Reconciled' : formatMinor(row.differenceMinor)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.some((row) => !row.hasOpeningBalance) ? (
        <p className="text-xs text-content-muted">
          Balances count from the first imported statement line. Until opening balances are entered
          (Books), they are movements since then, not what the bank shows today.
        </p>
      ) : null}
      {rows
        .filter((row) => row.openingAgrees === false && row.statementOpeningMinor !== null)
        .map((row) => (
          <p key={row.financialAccountId} className="text-xs text-warning">
            {row.accountName}: the statement's running balance says it opened at{' '}
            {formatMinor(row.statementOpeningMinor ?? '0')}, which is not the opening balance in the
            books. Check it in Books.
          </p>
        ))}
    </div>
  );
}

const BUCKETS: readonly ReceivableRow['bucket'][] = ['current', '31-60', '61-90', '90+'];
const BUCKET_LABELS: Readonly<Record<ReceivableRow['bucket'], string>> = {
  current: 'Up to 30 days',
  '31-60': '31 to 60 days',
  '61-90': '61 to 90 days',
  '90+': 'Over 90 days',
};

function Receivables({ rows }: { rows: readonly ReceivableRow[] }) {
  if (rows.length === 0) return null;
  return (
    <div className="flex flex-col gap-3">
      <table className="w-full text-sm tabular">
        <thead>
          <tr className="text-left text-xs text-content-muted">
            <th className="py-1 font-medium">Customer</th>
            <th className="py-1 font-medium">Invoice</th>
            <th className="py-1 text-right font-medium">Invoiced</th>
            <th className="py-1 text-right font-medium">Days</th>
            <th className="py-1 text-right font-medium">Owed</th>
          </tr>
        </thead>
        <tbody>
          {BUCKETS.map((bucket) => {
            const lines = rows.filter((row) => row.bucket === bucket);
            if (lines.length === 0) return null;
            return (
              <Fragment key={bucket}>
                <tr>
                  <td
                    colSpan={5}
                    className="pt-3 pb-1 text-xs font-medium uppercase text-content-muted"
                  >
                    {BUCKET_LABELS[bucket]}
                  </td>
                </tr>
                {lines.map((row) => (
                  <tr key={row.transactionId}>
                    <td className="py-0.5">{row.customerName ?? 'No customer on the invoice'}</td>
                    <td className="py-0.5 text-content-muted">
                      {row.invoiceNumber ?? '—'}
                      {row.status === 'draft' ? ' (draft)' : ''}
                    </td>
                    <td className="py-0.5 text-right">{formatMinor(row.invoicedMinor)}</td>
                    <td className="py-0.5 text-right">{String(row.daysOutstanding)}</td>
                    <td
                      className={[
                        'py-0.5 text-right font-medium',
                        bucket === '90+' ? 'text-danger' : '',
                      ].join(' ')}
                    >
                      {formatMinor(row.owedMinor)}
                    </td>
                  </tr>
                ))}
              </Fragment>
            );
          })}
          <tr className="border-t border-border font-semibold">
            <td colSpan={4} className="py-1">
              Total owed
            </td>
            <td className="py-1 text-right">
              {formatMinor(add(rows.map((row) => row.owedMinor)).toString())}
            </td>
          </tr>
        </tbody>
      </table>
      <p className="text-xs text-content-muted">
        What is owed comes from Housecall's own outstanding balance where it reports one, and from
        the deposits matched to the invoice otherwise. A deposit that has landed but is not yet
        matched on the Bank page still counts as owed here.
      </p>
    </div>
  );
}

/** Who we owe, how much, and how much of it is late, in a sentence. */
export function payablesSentence(rows: readonly PayableRow[]): string {
  if (rows.length === 0) {
    return 'Nothing is owed to any supplier at this date. Every bill is paid or was paid when bought.';
  }
  const total = add(rows.map((row) => row.owedMinor));
  const overdue = rows.filter((row) => (row.daysOverdue ?? 0) > 0);
  const late = add(overdue.map((row) => row.owedMinor));
  const suppliers = new Set(rows.map((row) => row.vendorId ?? row.transactionId)).size;
  const who = `${String(suppliers)} supplier${suppliers === 1 ? '' : 's'}`;
  return overdue.length === 0
    ? `We owe ${who} ${formatMinor(total.toString())} across ${String(rows.length)} bill${rows.length === 1 ? '' : 's'}, none of it past due.`
    : `We owe ${who} ${formatMinor(total.toString())}; ${formatMinor(late.toString())} of it is past due on ${String(overdue.length)} bill${overdue.length === 1 ? '' : 's'}.`;
}

const DUE_SOURCE_NOTE: Readonly<Record<NonNullable<PayableRow['dueOnSource']>, string>> = {
  document: '',
  terms: ' (from the terms)',
  assumed: ' (assumed, net 30)',
  person: '',
};

function Payables({ rows }: { rows: readonly PayableRow[] }) {
  if (rows.length === 0) return null;
  const groups = new Map<string, PayableRow[]>();
  for (const row of rows) {
    const key = row.vendorId ?? row.transactionId;
    groups.set(key, [...(groups.get(key) ?? []), row]);
  }
  return (
    <div className="flex flex-col gap-3">
      <table className="w-full text-sm tabular">
        <thead>
          <tr className="text-left text-xs text-content-muted">
            <th className="py-1 font-medium">Bill</th>
            <th className="py-1 font-medium">Dated</th>
            <th className="py-1 font-medium">Due</th>
            <th className="py-1 text-right font-medium">Billed</th>
            <th className="py-1 text-right font-medium">Owed</th>
          </tr>
        </thead>
        <tbody>
          {[...groups.entries()].map(([key, lines]) => (
            <Fragment key={key}>
              <tr>
                <td
                  colSpan={4}
                  className="pt-3 pb-1 text-xs font-medium uppercase text-content-muted"
                >
                  {lines[0]?.vendorName ?? 'No supplier on the bill'}
                </td>
                <td className="pt-3 pb-1 text-right text-xs font-medium text-content-muted">
                  {formatMinor(add(lines.map((row) => row.owedMinor)).toString())}
                </td>
              </tr>
              {lines.map((row) => {
                const overdue = (row.daysOverdue ?? 0) > 0;
                return (
                  <tr key={row.transactionId}>
                    <td className="py-0.5 text-content-muted">
                      {row.reference ?? '—'}
                      {row.status === 'draft' ? ' (draft)' : ''}
                    </td>
                    <td className="py-0.5">{row.issuedOn}</td>
                    <td className={['py-0.5', overdue ? 'text-danger' : ''].join(' ')}>
                      {row.dueOn === null
                        ? 'No due date'
                        : `${row.dueOn}${overdue ? `, ${String(row.daysOverdue)} days late` : ''}${
                            row.dueOnSource === null ? '' : DUE_SOURCE_NOTE[row.dueOnSource]
                          }`}
                    </td>
                    <td className="py-0.5 text-right">{formatMinor(row.billedMinor)}</td>
                    <td
                      className={[
                        'py-0.5 text-right font-medium',
                        overdue ? 'text-danger' : '',
                      ].join(' ')}
                    >
                      {formatMinor(row.owedMinor)}
                    </td>
                  </tr>
                );
              })}
            </Fragment>
          ))}
          <tr className="border-t border-border font-semibold">
            <td colSpan={4} className="py-1">
              Total owed
            </td>
            <td className="py-1 text-right">
              {formatMinor(add(rows.map((row) => row.owedMinor)).toString())}
            </td>
          </tr>
        </tbody>
      </table>
      <p className="text-xs text-content-muted">
        A bill is owed from the day its document says it was not paid at the counter until a bank
        line matched to it on the Bank page pays it. A payment that has left the bank but is not yet
        matched still counts as owed here. A due date nobody printed is assumed at net 30 and says
        so; set the real one on the record.
      </p>
    </div>
  );
}

function TrialBalance({ rows }: { rows: readonly TrialBalanceRow[] }) {
  if (rows.length === 0) {
    return <p className="text-sm text-content-muted">Nothing is in the books at this date yet.</p>;
  }
  const sorted = [...rows].sort(
    (a, b) =>
      ACCOUNT_TYPE_ORDER.indexOf(a.accountType) - ACCOUNT_TYPE_ORDER.indexOf(b.accountType) ||
      (a.accountCode ?? '').localeCompare(b.accountCode ?? ''),
  );
  return (
    <table className="w-full text-sm tabular">
      <thead>
        <tr className="text-left text-xs text-content-muted">
          <th className="py-1 font-medium">Account</th>
          <th className="py-1 text-right font-medium">Debit</th>
          <th className="py-1 text-right font-medium">Credit</th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((row) => (
          <tr key={row.accountId}>
            <td className="py-0.5">
              <Label code={row.accountCode} name={row.accountName} />
            </td>
            <td className="py-0.5 text-right">
              {row.debitMinor === '0' ? '' : formatMinor(row.debitMinor)}
            </td>
            <td className="py-0.5 text-right">
              {row.creditMinor === '0' ? '' : formatMinor(row.creditMinor)}
            </td>
          </tr>
        ))}
        <tr className="border-t border-border font-semibold">
          <td className="py-1">Totals</td>
          <td className="py-1 text-right">
            {formatMinor(add(rows.map((row) => row.debitMinor)).toString())}
          </td>
          <td className="py-1 text-right">
            {formatMinor(add(rows.map((row) => row.creditMinor)).toString())}
          </td>
        </tr>
      </tbody>
    </table>
  );
}
